import React from 'react';

import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { reverse } from 'named-urls';

import Skeleton from '@mui/material/Skeleton';
import TablePagination from '@mui/material/TablePagination';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SearchAdornment from 'components/Adornment/SearchAdornment';
import GroupTypeChip from 'components/Chips/GroupTypeChip';
import { StyledTextField } from 'components/Input/styles';

import DataError from 'containers/Error/DataError';

import routes from 'core/routes';

import { UserMemberGroupRow } from 'utils/api/modules/memberGroups';

import { useStore } from 'utils/hooks/store';

import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import { PAGE_SIZE_OPTIONS, SEARCH_THRESHOLD, useUserGroups } from './useUserGroups';

import {
  Chevron,
  EmptyNote,
  GroupRow,
  GroupRowName,
  GroupRowText,
  PaginatedGroupList,
  PaginationWrap,
  SearchWrap,
  SkeletonRows,
  StaticGroupRow,
  TypeChipCell,
} from './styles';

const SKELETON_ROW_COUNT = 3;

export interface GroupMembershipListProps {
  userUuid: string;
  emptyText: string;
}

const GroupMembershipList: React.FunctionComponent<GroupMembershipListProps> = ({ userUuid, emptyText }) => {
  const { session } = useStore();
  const navigate = useNavigate();
  const {
    rows,
    count,
    totalGroups,
    isLoading,
    hasError,
    query,
    debouncedQuery,
    page,
    rowsPerPage,
    onQueryChange,
    onPageChange,
    onRowsPerPageChange,
    reload,
  } = useUserGroups(userUuid);

  // The group detail route is manager-only, so a User-role viewer's rows lead nowhere.
  const canManage = !!session.user && ADMIN_AND_ACCOUNT_MANAGERS.includes(session.user.role);

  const showSearch = totalGroups > SEARCH_THRESHOLD;
  // Against the smallest page size, not the current one — the footer is also the rows-per-page control.
  const showPagination = count > PAGE_SIZE_OPTIONS[0];

  const renderRow = (row: UserMemberGroupRow): React.ReactElement => {
    const content = (
      <>
        <TypeChipCell>
          <GroupTypeChip type={row.membershipMode} />
        </TypeChipCell>
        <GroupRowText>
          <GroupRowName>{row.name}</GroupRowName>
        </GroupRowText>
        {canManage && (
          <Chevron>
            <ChevronRightIcon fontSize="small" />
          </Chevron>
        )}
      </>
    );

    return canManage ? (
      <GroupRow
        key={row.uuid}
        type="button"
        data-testid={`user-group-row-${row.uuid}`}
        onClick={() => navigate(reverse(routes.dashboard.settings.groups.details, { groupId: row.uuid }))}
      >
        {content}
      </GroupRow>
    ) : (
      <StaticGroupRow key={row.uuid} data-testid={`user-group-row-${row.uuid}`}>
        {content}
      </StaticGroupRow>
    );
  };

  const search = showSearch && (
    <SearchWrap>
      <StyledTextField
        fullWidth
        size="small"
        placeholder="Search groups…"
        inputProps={{ 'data-testid': 'user-groups-search', 'aria-label': 'Search groups' }}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        InputProps={{ startAdornment: <SearchAdornment fontSize="small" /> }}
      />
    </SearchWrap>
  );

  if (isLoading && rows.length === 0) {
    return (
      <>
        {search}
        <SkeletonRows>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </SkeletonRows>
      </>
    );
  }

  if (hasError) {
    return <DataError onReload={reload} message="We are unable to load groups" />;
  }

  if (rows.length === 0) {
    const filtered = debouncedQuery.trim();
    return (
      <>
        {search}
        <EmptyNote data-testid="user-groups-empty">{filtered ? `No groups match “${filtered}”.` : emptyText}</EmptyNote>
      </>
    );
  }

  return (
    <>
      {search}
      <PaginatedGroupList data-testid="user-groups-list">{rows.map(renderRow)}</PaginatedGroupList>
      {showPagination && (
        <PaginationWrap>
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, next) => onPageChange(next)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            labelRowsPerPage="Groups per page"
          />
        </PaginationWrap>
      )}
    </>
  );
};

export default observer(GroupMembershipList);
